<template>
    <transition name="fade">
        <div class="modal is-active" v-show="visible">
            <div class="modal-background" @click="handleCancel"></div>

            <div class="modal-content">
                <div class="popup-dialog">
                    <div class="popup-title">
                        <h1 class="title is-1">{{ modalTitle }}</h1>
                        <div class="popup-close" @click.prevent="handleCancel">
                            <i class="icon">close</i>
                        </div>
                    </div>

                    <div class="popup-body popup-body-bottom-border">
                        <p>{{ modalDescription }}</p>
                    </div>

                    <!-- Loading State -->
                    <div v-if="isInitializing" class="has-text-centered">
                        <b-loading :is-full-page="false" :active="true"/>
                        <p class="mt-4">{{ $t('auth.verification.loading') }}</p>
                    </div>

                    <div v-else-if="showMethodSelection && availableMethods.length > 0"
                         class="popup-body popup-security-input popup-security-methods"
                    >
                        <label
                            v-for="method in availableMethods"
                            :key="method.method"
                            class="two-factor-method"
                            :class="{ 'is-selected': selectedMethod === method.method }"
                        >
                            <input
                                type="radio"
                                :value="method.method"
                                v-model="selectedMethod"
                            />
                            <div class="method-content">
                                <div class="method-title">{{ method.name }}</div>
                                <div class="method-description">{{ method.description }}</div>
                            </div>
                        </label>
                    </div>

                    <div v-else-if="isRateLimited || currentMethod" class="popup-body popup-security-input">
                        <security-visual
                            ref="securityVisual"
                            :polling="isPolling"
                        />

                        <!-- Auth method inputs / info -->
                        <div v-if="methodType === 'code'" class="code-input-container">
                            <code-input
                                ref="codeInput"
                                v-model="fullCode"
                                :disabled="isLoading"
                                :hidden="isUnlocking || isUnlocked"
                                :auto-submit="true"
                                @submit="handleSubmit"
                            />
                        </div>
                        <div v-else-if="methodType === 'redirect'" class="redirect-container"
                             :class="{'is-hidden': isUnlocking || isUnlocked}">
                            <p class="has-text-centered redirect-link">
                                <a :href="currentMethod.metadata.redirect_url"
                                   target="_blank"
                                   rel="noopener"
                                   @click="handleRedirectClick">
                                    {{ $t('auth.verification_redirect.redirect_link_text') }}
                                </a>
                            </p>
                        </div>

                        <!-- Resend / rate limit messages (only for methods that support resend) -->
                        <template v-if="currentMethod?.metadata?.can_resend">
                            <p v-if="isRateLimited" class="sent-again is-rate-limited"
                               :class="{'is-hidden': isUnlocking || isUnlocked}">
                                {{ rateLimitMessage }}
                                {{ $tc('auth.verification_code.code_resend_in_seconds', resendInSeconds) }}
                            </p>
                            <p v-else-if="resendInSeconds > 0" class="sent-again"
                               :class="{'is-hidden': isUnlocking || isUnlocked}">
                                {{ $t('auth.verification_code.code_not_received') }}
                                {{ $tc('auth.verification_code.code_resend_in_seconds', resendInSeconds) }}
                            </p>
                            <p v-else class="sent-again" :class="{'is-hidden': isUnlocking || isUnlocked}">
                                {{ $t('auth.verification_code.code_not_received') }}
                                <a href="#" @click.prevent="handleResend">{{
                                        $t('auth.verification_code.code_resend_link')
                                    }}</a>
                            </p>
                        </template>
                    </div>

                    <div class="popup-footer">
                        <a v-if="showMethodSelection && availableMethods.length > 0" href="#"
                           @click.prevent="handleManageOptions" class="other-option-link">
                            {{ $t('auth.verification_code.manage') }}
                        </a>
                        <a v-else-if="currentMethod && availableMethods.length > 1" href="#"
                           @click.prevent="showMethodSelection = true" class="other-option-link">
                            {{ $t('auth.verification_code.different_method') }}
                        </a>

                        <a @click.prevent="handleCancel" href="#" role="button"
                           v-if="!isUnlocking && !isUnlocked">{{ $t('confirmation_dialog.deny') }}</a>
                        <g-button :loading="isLoading"
                                  :disabled="submitButtonDisabled"
                                  @click.prevent="handleSubmit">
                            {{ $t('confirmation_dialog.confirm') }}
                        </g-button>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import Base from "../../Base.vue";
import CodeInput from "../../../auth/CodeInput.vue";
import SecurityVisual from "../../../auth/SecurityVisual.vue";
import SecurityAnimationMixin from "../../../auth/SecurityAnimationMixin.js";
import TwoFactorService from "../../../../services/TwoFactorService.js";

export default {
    components: {CodeInput, SecurityVisual},
    mixins: [Base, SecurityAnimationMixin],
    props: {
        verificationType: {
            type: String,
            required: true,
        },
        verificationToken: {
            type: String,
        },
        methods: {
            type: Array,
            default: () => []
        },
        endpoints: {
            type: Object,
            default: () => ({})
        },
        retryAfter: {
            type: Number,
            default: 60
        },
        context: {
            type: Object,
            default: () => ({})
        },
        title: String,
        instructions: String,
        expiresAt: String,
        onVerified: Function,
        onCancel: Function
    },
    data() {
        return {
            // State (isUnlocking + isUnlocked come from SecurityAnimationMixin)
            isInitializing: true,
            isLoading: false,
            isResending: false,
            showMethodSelection: false,
            hasError: false,

            // Rate limiting
            isRateLimited: false,
            rateLimitMessage: null,
            rateLimitRetryIn: 0,

            // Methods
            availableMethods: [],
            selectedMethod: null,
            currentMethod: null,

            // Verification
            fullCode: '',
            errorMessage: null,

            // Timers
            resendInSeconds: 20,
            resendTimer: null,
            rateLimitTimer: null,

            // Polling
            pollingInterval: null,
            pollingTransactionId: null,
            pollingExpiresAt: null,
            lastPollAt: null,
            visibilityHandler: null,
            isPolling: false,
        }
    },
    computed: {
        modalTitle() {
            if (this.showMethodSelection) {
                return this.$t('auth.verification.select_method_title');
            }

            if (this.title) {
                return this.title;
            }

            return this.$t('auth.verification.title');
        },
        modalDescription() {
            if (this.showMethodSelection) {
                return this.$t('auth.verification.select_method_description');
            }

            if (this.instructions) {
                return this.instructions;
            }

            if (this.currentMethod?.metadata?.instructions) {
                return this.currentMethod?.metadata?.instructions
            }

            return this.$t('auth.verification.description');
        },
        methodType() {
            if (this.currentMethod?.metadata?.redirect_url) {
                return 'redirect';
            }

            return 'code';
        },
        codeType() {
            return this.currentMethod?.metadata?.code_type || 'numeric';
        },
        submitButtonDisabled() {
            if (this.showMethodSelection) {
                return this.selectedMethod === null;
            }

            // For redirect methods (polling), disable submit button entirely
            if (this.methodType === 'redirect') {
                return true;
            }

            return this.isUnlocked || this.isUnlocking || !this.isValidCodeFormat || this.isRateLimited
        },
        isValidCodeFormat() {
            return this.fullCode.length === 6 && /^\d{6}$/.test(this.fullCode)
        },
    },
    methods: {
        async initialize() {
            try {
                this.availableMethods = this.methods;

                // Find selected/auto-initiated method
                const selected = this.availableMethods.find(m => m.is_selected) || this.availableMethods[0];

                if (selected && selected.auto_initiated) {
                    await this.setCurrentMethod(selected);
                } else if (selected && selected.is_selected && !selected.requires_initiation) {
                    // Pre-selected method that doesn't require initiation (e.g. TOTP)
                    await this.setCurrentMethod(selected);
                } else if (this.availableMethods.length === 1) {
                    // Single method: set it as selected and initiate (switch endpoint call)
                    this.selectedMethod = selected.method;
                    await this.initiateMethod();
                } else {
                    // Multiple methods - show selection
                    this.showMethodSelection = true;
                }

            } catch (error) {
                console.error('Failed to initialize verification:', error);
                this.$buefy.toast.open({
                    message: error.response?.data?.error || error.message || this.$t('errors.generic'),
                    type: 'is-danger'
                });
                this.handleCancel();
            } finally {
                this.isInitializing = false;
            }
        },

        async handleSubmit() {
            // If in method selection mode, initiate the selected method
            if (this.showMethodSelection) {
                return await this.initiateMethod();
            }

            // Otherwise, verify the code
            await this.verifyCode();
        },

        async setCurrentMethod(methodObject) {
            this.currentMethod = methodObject;
            this.selectedMethod = methodObject.method
            this.showMethodSelection = false;

            if (methodObject.metadata?.can_resend) {
                this.startResendCountdown();
            }

            if (methodObject.metadata?.supports_polling && methodObject.metadata?.transaction_id) {
                this.startPolling(methodObject);
            }

            // Auto-open redirect URL in new tab for external services (e.g., Duo)
            if (methodObject.metadata?.redirect_url) {
                window.open(methodObject.metadata.redirect_url, '_blank', 'noopener');
            }
        },

        /**
         * Handle click on redirect link and restart polling
         * This is important for retry scenarios where the external verification
         * failed and the user clicks the link again
         */
        handleRedirectClick() {
            // Restart polling if the method supports it
            if (this.currentMethod?.metadata?.supports_polling && this.currentMethod?.metadata?.transaction_id) {
                this.startPolling(this.currentMethod);
            }
            // Allow default link behavior (opens in new tab via href)
        },

        async initiateMethod() {
            if (!this.selectedMethod) return;

            this.isLoading = true;
            this.errorMessage = null;
            this.hasError = false;

            try {
                const method = this.availableMethods.find(m => m.method === this.selectedMethod);

                if (!method.requires_initiation) {
                    // No need to call the switch endpoint
                    await this.setCurrentMethod(method)
                    return;
                }

                // Switch to the selected method
                const result = await TwoFactorService.switch(
                    this.selectedMethod,
                    this.getEndpoint('switch')
                );

                if (TwoFactorService.isSuccess(result)) {
                    await this.setCurrentMethod(result.data.method)
                } else if (result.status === 429) {
                    this.handleRateLimitError(result);
                } else if (result.message) {
                    this.$buefy.toast.open({
                        message: result.message,
                        type: 'is-danger'
                    });
                }
            } catch (err) {
                console.warn('Error switching verification method', err);
            } finally {
                this.isLoading = false;
            }
        },

        async verifyCode() {
            if (this.submitButtonDisabled) {
                return;
            }

            this.isLoading = true
            this.errorMessage = null;
            this.hasError = false;

            const result = await TwoFactorService.verify(
                this.currentMethod.method,
                this.getEndpoint('verify'),
                {
                    code: this.fullCode,
                    type: this.verificationType,
                }
            );

            if (TwoFactorService.isSuccess(result)) {
                this.handleVerificationSuccess(result.data);
            } else if (result.status === 429) {
                this.handleRateLimitError(result);
            } else {
                this.handleVerificationError(result);
            }

            this.isLoading = false;
        },

        handleVerificationSuccess(data) {
            // Start unlock animation
            this.triggerUnlock();

            const verificationData = {
                verified: true,
                expiresAt: data.expires_at,
            };

            // Give user time to see the success animation
            setTimeout(() => {
                // Call the onVerified callback if provided
                if (typeof this.onVerified === 'function') {
                    this.onVerified(verificationData);
                }

                // Also emit for any local listeners
                this.$emit('verificationVerified', verificationData);

                this.destroy({
                    ...verificationData
                });
            }, 1200);
        },

        handleCancel() {
            // Call the onCancel callback if provided
            if (typeof this.onCancel === 'function') {
                this.onCancel();
            }

            // Destroy with cancelled flag
            this.destroy({
                verified: false,
                cancelled: true
            });
        },

        async handleManageOptions() {
            // If we are not on the settings.account.security page, navigate there
            if (this.$route.name !== 'settings.account.security') {
                await this.$router.push({name: 'settings.account.security'});
            }

            // Call the onCancel callback if provided
            if (typeof this.onCancel === 'function') {
                this.onCancel();
            }

            // Destroy with cancelled flag
            this.destroy({
                verified: false,
                cancelled: true
            });
        },

        handleVerificationError(errorData) {
            this.triggerDeny();

            // Notice with the message
            if (errorData.message) {
                this.$buefy.toast.open({
                    message: errorData.message,
                    type: 'is-danger'
                });
            }

            // Clear code on certain errors
            this.clearCodeInput();
        },

        handleRateLimitError(errorData) {
            this.triggerDeny();

            const retryAfterSeconds = errorData.retry_after || 60;

            this.isRateLimited = true;
            this.rateLimitMessage = errorData.message;

            // Start countdown with the server-provided retry_after value
            this.startRateLimitCountdown(retryAfterSeconds);

            // Clear the code inputs
            this.clearCodeInput();

            // Show toast notification
            this.$buefy.toast.open({
                message: errorData.message,
                type: 'is-danger',
                duration: 5000
            });
        },

        async handleResend() {
            const result = await TwoFactorService.resend(
                this.currentMethod.method,
                this.getEndpoint('resend')
            );

            if (TwoFactorService.isSuccess(result)) {
                // Reset countdown
                this.startResendCountdown();
                this.clearCodeInput();

                // Show success message
                if (result.data.message) {
                    this.$buefy.toast.open({
                        message: result.data.message,
                        type: 'is-success'
                    });
                }
            } else if (result.status === 429) {
                this.handleRateLimitError(result);
            } else if (result.message) {
                this.$buefy.toast.open({
                    message: result.message,
                    type: 'is-danger'
                });
            }
        },

        getEndpoint(type) {
            // Use endpoints from the 428 response
            return this.endpoints[type];
        },

        startResendCountdown() {
            // Clear any existing interval
            if (this.resendInterval) {
                clearInterval(this.resendInterval);
            }

            this.resendInSeconds = this.retryAfter;
            this.resendInterval = setInterval(() => {
                if (this.resendInSeconds > 0) {
                    this.resendInSeconds--;
                } else {
                    clearInterval(this.resendInterval);
                    this.resendInterval = null;
                }
            }, 1000);
        },

        startRateLimitCountdown(seconds) {
            // Clear any existing interval
            if (this.resendInterval) {
                clearInterval(this.resendInterval);
            }

            this.resendInSeconds = seconds;
            this.resendInterval = setInterval(() => {
                if (this.resendInSeconds > 0) {
                    this.resendInSeconds--;
                } else {
                    clearInterval(this.resendInterval);
                    this.resendInterval = null;
                    this.isRateLimited = false;
                    this.rateLimitMessage = null;
                }
            }, 1000);
        },

        clearTimers() {
            if (this.resendTimer) {
                clearInterval(this.resendTimer);
                this.resendTimer = null;
            }

            if (this.rateLimitTimer) {
                clearInterval(this.rateLimitTimer);
                this.rateLimitTimer = null;
            }

            this.stopPolling();
        },

        /**
         * Start polling for external service verification status
         * Uses a combination of interval-based polling and visibility change detection
         * for optimal responsiveness when user returns from external auth (e.g., Duo)
         */
        startPolling(methodObject) {
            // Stop any existing polling
            this.stopPolling();

            const transactionId = methodObject.metadata.transaction_id;
            // Default to 10 seconds for background polling (visibility change handles immediate checks)
            const pollIntervalMs = (methodObject.metadata.poll_interval_seconds || 10) * 1000;
            const expiresAt = methodObject.metadata.expires_at ? new Date(methodObject.metadata.expires_at) : null;

            this.pollingTransactionId = transactionId;
            this.pollingExpiresAt = expiresAt;
            this.lastPollAt = null;
            this.isPolling = true;

            // Set up visibility change handler for immediate check when tab becomes visible
            this.visibilityHandler = this.handleVisibilityChange.bind(this);
            document.addEventListener('visibilitychange', this.visibilityHandler);

            // Start background polling interval
            this.pollingInterval = setInterval(async () => {
                await this.pollVerificationStatus();
            }, pollIntervalMs);
        },

        /**
         * Handle visibility change - poll immediately when tab becomes visible
         * Throttled to prevent excessive requests from rapid tab switching
         */
        async handleVisibilityChange() {
            if (document.visibilityState !== 'visible') {
                return;
            }

            if (!this.pollingTransactionId) {
                return;
            }

            // Throttle: minimum 1 second between polls to prevent rapid-fire requests
            const minPollIntervalMs = 1000;
            const now = Date.now();

            if (this.lastPollAt && (now - this.lastPollAt) < minPollIntervalMs) {
                return;
            }

            await this.pollVerificationStatus();
        },

        /**
         * Stop polling for verification status
         */
        stopPolling() {
            if (this.pollingInterval) {
                clearInterval(this.pollingInterval);
                this.pollingInterval = null;
            }

            if (this.visibilityHandler) {
                document.removeEventListener('visibilitychange', this.visibilityHandler);
                this.visibilityHandler = null;
            }

            this.pollingTransactionId = null;
            this.pollingExpiresAt = null;
            this.lastPollAt = null;
            this.isPolling = false;
        },

        /**
         * Poll the backend for verification status
         */
        async pollVerificationStatus() {
            if (!this.pollingTransactionId || !this.currentMethod) {
                return;
            }

            // Check expiration before making request
            if (this.pollingExpiresAt && new Date() > this.pollingExpiresAt) {
                this.stopPolling();
                this.$buefy.toast.open({
                    message: this.$t('auth.verification.status.expired'),
                    type: 'is-danger'
                });
                return;
            }

            // Track last poll time for throttling
            this.lastPollAt = Date.now();

            const result = await TwoFactorService.poll(
                this.currentMethod.method,
                this.getEndpoint('poll'),
                this.pollingTransactionId
            );

            // 202 Accepted means still pending
            if (TwoFactorService.isPending(result)) {
                return;
            }

            // Success - verification completed (2xx non-pending response)
            if (TwoFactorService.isSuccess(result)) {
                this.stopPolling();
                this.handleVerificationSuccess(result.data);
                return;
            }

            // Error responses
            if (result.status === 429) {
                this.stopPolling();
                this.handleRateLimitError(result);
                return;
            }

            if (TwoFactorService.isError(result)) {
                this.stopPolling();

                if (result.message) {
                    this.$buefy.toast.open({
                        message: result.message,
                        type: 'is-danger'
                    });
                }
            }
        },
    },

    async mounted() {
        await this.initialize();
    },

    beforeDestroy() {
        this.clearTimers();
    },
}
</script>

<style lang="scss" scoped>
.popup-security-input {
    .sent-again {
        font-size: 0.875rem;
        text-align: center;

        &.is-rate-limited {
            color: #DE134E;
        }
    }

    .redirect-container {
        margin-bottom: 1rem;

        .redirect-link {
            font-size: 0.875rem;
        }
    }
}

.popup-footer {

    .other-option-link {
        margin-right: auto;
        gap: 0.5rem;
    }
}

.popup-security-methods {

    .two-factor-method {
        display: flex;
        cursor: pointer;
        padding: 1.25rem 1.5625rem;
        margin-bottom: 0.75rem;
        width: 100%;
        align-items: flex-start;
        gap: 1.25rem;
        border: 1px solid #FFF;
        background: #FFF;
        border-radius: 0.625rem;
        transition: all 0.2s ease;

        &:hover {
            border-color: #3BC173;
        }

        &.is-selected {
            border-color: #3BC173;

            input[type="radio"] {
                &::before {
                    background-color: #3BC173;
                    transform: scale(1);
                }
            }
        }

        input[type="radio"] {
            flex-shrink: 0;
            width: 26px;
            height: 26px;
            align-self: center;
            margin: 0;
            padding: 0;
            appearance: none;
            border-radius: 50%;
            position: relative;
            box-shadow: 0 0 2px rgba(0, 0, 0, 0.3);

            &::before {
                content: '';
                position: absolute;
                top: 0.25rem;
                left: 0.25rem;
                width: 1.1rem;
                height: 1.1rem;
                border-radius: 50%;
                background-color: transparent;
                transform: scale(0);
                transition: transform 0.2s ease;
            }

            &:checked {
                border-color: #2CC36B;
            }
        }

        .method-content {
            display: flex;
            padding-right: 0.625rem;
            flex-direction: column;
            align-items: flex-start;
            flex: 1 0 0;
            align-self: stretch;
        }

        .method-title {
            color: #1F1F1F;
            font-size: 1rem;
            font-style: normal;
            font-weight: 600;
            line-height: normal;
        }

        .method-description {
            color: #6A7272;
            font-size: 0.875rem;
            font-style: normal;
            font-weight: 400;
            line-height: normal;
        }
    }
}
</style>
